import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "cgu-error"
}
const _hoisted_2 = { role: "alert" }

import { ref, computed, onBeforeUnmount, toRefs, watch } from 'vue';
import { useFocus } from '@vueuse/core';
import { QCheckbox } from 'quasar';

// Stores
import { useUiStore } from '@stores/ui';
import { useSecurityStore } from '@stores/security';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// Personal Components
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import DiagGeneric from '@components/dialog/DiagGeneric.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';
import QCustomInput from '@components/form/QCustomInput.vue';
import MentionsLegalesPage from '@pages/terms/MentionsLegalesPage.vue';
import CGUPage from '@pages/terms/CGUPage.vue';

// Personal libs
import { $t } from 'src/i18n/utils';
import { IConfigSecurity } from '@shared/interface/config.interface';
import {
  displayDialogError,
  displayDialogSuccess
} from '@shared/lib/notify.lib';
import signupRepository from '@data/repository/signup.repository';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';
import { SecretTypeEnum } from '@shared/enum/secret-type.enum'
import { InputFiedTypeEnum } from '@shared/enum/input.enum'
import { FormNameEnum } from '@shared/enum/form-name.enum';

/**
 * Stores
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DialogSignup',
  props: {
  show: { required: true, type: Boolean, default: false },
},
  emits: ['close'],
  setup(__props, { emit: __emit }) {

// VENDOR
const uiStore = useUiStore();
const securityStore = useSecurityStore();

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * HTMLElement refs
 */
const qSignupDescription = ref<HTMLInputElement>();
const qInputUsername = ref();
const qInputEmail = ref();
const qInputPassword = ref();
const qInputPasswordConfirm = ref();
const qCheckboxCgu = ref<QCheckbox>()
const { focused: qCheckboxCguFocused } = useFocus(qCheckboxCgu)

/**
 * Emits
 */
const emits = __emit;

/**
 * Data
 */
const showPassword = ref<boolean>(false);
const showPasswordConfirm = ref<boolean>(false);
const showMentionsLegales = ref<boolean>(false);
const showCgu = ref<boolean>(false);
const chkAcceptError = ref<boolean | undefined>(false)
const loading = ref<boolean | undefined>(false);

const username = ref<string | undefined>(undefined);
const email = ref<string | undefined>(undefined);
const password = ref<string | undefined>(undefined);
const passwordConfirm = ref<string | undefined>(undefined);
const chkAcceptRules = ref<boolean>(false);

/**
 * Computed data
 */
const securityRules = computed<IConfigSecurity | undefined>(
  () => uiStore.config?.security,
);

/**
 * Properties
 */
const props = __props;
const { show } = toRefs(props);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

function resetValidations() {
    // Reset focus
    qInputUsername.value?.resetValidation()
    qInputEmail.value?.resetValidation()
    qInputPassword.value?.resetValidation()
    qInputPasswordConfirm.value?.resetValidation()
    chkAcceptError.value = false
}

function validForm(): boolean {
  // Reset focus
  qInputUsername.value?.blur()
  qInputEmail.value?.blur()
  qInputPassword.value?.blur()
  qInputPasswordConfirm.value?.blur()

  // Validate
  qInputUsername.value?.validate()
  qInputEmail.value?.validate()
  qInputPassword.value?.validate()
  qInputPasswordConfirm.value?.validate()

  if (!chkAcceptRules.value) {
    chkAcceptError.value = true
  }

  if (qInputUsername.value != null && qInputUsername.value.hasError()) {
    qInputUsername.value.focus()
    return false;
  }

  if (qInputEmail.value != null && qInputEmail.value.hasError()) {
    qInputEmail.value.focus()
    return false;
  }

  if (qInputPassword.value != null && qInputPassword.value.hasError()) {
    qInputPassword.value.focus()
    return false;
  }

  if (qInputPasswordConfirm.value != null && qInputPasswordConfirm.value.hasError()) {
    qInputPasswordConfirm.value.focus()
    return false;
  }

  if (password.value != passwordConfirm.value) {
    displayDialogError({
      message:
        'Le mot de passe et sa confirmation doivent être strictement identiques',
    });
    return false;
  }

  if (!chkAcceptRules.value) {
    qCheckboxCguFocused.value = true
    return false;
  }

  return true;
}

async function onOkClick() {
  if (!validForm()) {
    return;
  }

  loading.value = true;
  chkAcceptError.value = false

  // Call api endpoint
  signupRepository
    .signup({
      username: username.value ?? '',
      email: email.value ?? '',
      password: password.value ?? '',
      passwordConfirm: passwordConfirm.value ?? '',
      chkAcceptRules: chkAcceptRules.value,
      csrf: (await securityStore.getCsrf(FormNameEnum.SIGNUP))?.token
    })
    .then(() => {
      displayDialogSuccess({
        message:
          'Demande confirmée. Vous allez recevoir un mail vous invitant à valider votre adresse email.',
      });

      onClose();
    })
    .catch((error: Error) => {
      displayDialogError({
        message: error.message ?? 'null',
        isHtml: true,
      })
    })
    .finally(() => {
      clearForms();
      resetValidations()
      loading.value = false
    });
}

function onCancelClick() {
  // Forms validations
  resetValidations()

  onClose();
}

function clearForms() {
  username.value = undefined;
  email.value = undefined;
  password.value = undefined;
  passwordConfirm.value = undefined;
  chkAcceptRules.value = false;
  showPassword.value = false;
  showPasswordConfirm.value = false;
}

/**
 * Watch
 */
watch(
  () => show.value,
  (newValue: boolean | undefined) => {
    if (newValue) {
      clearForms();
    }
  },
);

/**
 * Hooks
 */
onBeforeUnmount(() => {
  password.value = undefined;
  passwordConfirm.value = undefined;
  loading.value = undefined;
});

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(QDialogTemplate, {
      "show-dialog": _unref(show),
      title: _unref($t)('signup.title'),
      "display-close-button": true,
      maximized: _unref(isMobile),
      persistent: true,
      "dialog-size": _unref(DialogSizeEnum).SMALL,
      onClose: _cache[8] || (_cache[8] = ($event: any) => (onClose()))
    }, {
      content: _withCtx(() => [
        _createVNode(_component_q_card, {
          flat: "",
          class: "no-border-radius q-pt-sm"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_card_section, {
              class: "q-pt-none",
              tabindex: "-1",
              ref_key: "qSignupDescription",
              ref: qSignupDescription
            }, {
              default: _withCtx(() => _cache[15] || (_cache[15] = [
                _createTextVNode(" La demande de création de compte nécessite la saisie : "),
                _createElementVNode("ul", { class: "q-my-none" }, [
                  _createElementVNode("li", null, " d'un nom d'utilisateur : il est conseillé d'indiquer soit votre prénom soit votre prénom et l'initiale de votre nom, par exemple : \"Brice L\" ou \"Brice L.\" ou \"Brice\", "),
                  _createElementVNode("li", null, " d'une adresse email qui servira d'identifiant de connexion, "),
                  _createElementVNode("li", null, " d'un mot de passe de connexion. ")
                ], -1 /* HOISTED */)
              ])),
              _: 1 /* STABLE */
            }, 512 /* NEED_PATCH */),
            _createVNode(_component_q_card_section, { class: "text-italic q-my-none q-py-none" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref($t)('generic.form.mandatoryFields')), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_q_card_section, { class: "q-pa-none q-pt-none" }, {
              default: _withCtx(() => [
                _createCommentVNode(" Input : username "),
                _createVNode(QCustomInput, {
                  modelValue: username.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((username).value = $event)),
                  ref_key: "qInputUsername",
                  ref: qInputUsername,
                  "extra-class": 'q-mx-sm',
                  disable: loading.value,
                  "on-enter-key": onOkClick,
                  "input-type": _unref(InputFiedTypeEnum).USERNAME
                }, null, 8 /* PROPS */, ["modelValue", "disable", "input-type"]),
                _createCommentVNode(" Input : email "),
                _createVNode(QCustomInput, {
                  modelValue: email.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((email).value = $event)),
                  ref_key: "qInputEmail",
                  ref: qInputEmail,
                  "extra-class": 'q-mx-sm',
                  disable: loading.value,
                  "on-enter-key": onOkClick,
                  "input-type": _unref(InputFiedTypeEnum).EMAIL
                }, null, 8 /* PROPS */, ["modelValue", "disable", "input-type"]),
                _createCommentVNode(" Input : password "),
                _createVNode(QCustomInput, {
                  modelValue: password.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((password).value = $event)),
                  ref_key: "qInputPassword",
                  ref: qInputPassword,
                  "extra-class": 'q-mx-sm',
                  disable: loading.value,
                  "on-enter-key": onOkClick,
                  "input-type": _unref(InputFiedTypeEnum).PASSWORD,
                  "secret-type": _unref(SecretTypeEnum).NORMAL,
                  "enable-pass-checker": true
                }, null, 8 /* PROPS */, ["modelValue", "disable", "input-type", "secret-type"]),
                _createCommentVNode(" Input : password confirm "),
                _createVNode(QCustomInput, {
                  modelValue: passwordConfirm.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((passwordConfirm).value = $event)),
                  ref_key: "qInputPasswordConfirm",
                  ref: qInputPasswordConfirm,
                  "extra-class": 'q-mx-sm',
                  disable: loading.value,
                  "on-enter-key": onOkClick,
                  "input-type": _unref(InputFiedTypeEnum).PASSWORD,
                  "secret-type": _unref(SecretTypeEnum).CONFIRMATION
                }, null, 8 /* PROPS */, ["modelValue", "disable", "input-type", "secret-type"])
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_q_card_section, { class: "" }, {
              default: _withCtx(() => [
                _cache[16] || (_cache[16] = _createTextVNode(" Pour que la demande de création soit effective, vous devez lire et accepter les ")),
                _createVNode(_component_q_btn, {
                  class: "q-py-none q-px-xs q-ma-none accept-button",
                  style: {"min-height":"0px"},
                  flat: "",
                  "no-caps": "",
                  label: "mentions légales",
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (showMentionsLegales.value = true))
                }),
                _cache[17] || (_cache[17] = _createTextVNode(" ainsi que les ")),
                _createVNode(_component_q_btn, {
                  class: "q-py-none q-px-xs q-ma-none accept-button",
                  style: {"min-height":"0px"},
                  flat: "",
                  "no-caps": "",
                  label: "conditions générales d'utilisation",
                  onClick: _cache[5] || (_cache[5] = ($event: any) => (showCgu.value = true))
                }),
                _cache[18] || (_cache[18] = _createTextVNode(". "))
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_q_card_section, { class: "q-pt-none" }, {
              default: _withCtx(() => [
                _createCommentVNode(" Signup accept "),
                _createVNode(_unref(QCheckbox), {
                  class: "set-outline",
                  ref_key: "qCheckboxCgu",
                  ref: qCheckboxCgu,
                  modelValue: chkAcceptRules.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((chkAcceptRules).value = $event)),
                  disable: loading.value,
                  label: _unref($t)('signup.cgu.checkboxTitle'),
                  onClick: _cache[7] || (_cache[7] = ($event: any) => (chkAcceptError.value = !chkAcceptRules.value))
                }, null, 8 /* PROPS */, ["modelValue", "disable", "label"]),
                (chkAcceptError.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                      _createElementVNode("div", _hoisted_2, _toDisplayString(_unref($t)('signup.cgu.error')), 1 /* TEXT */)
                    ]))
                  : _createCommentVNode("v-if", true)
              ]),
              _: 1 /* STABLE */
            })
          ]),
          _: 1 /* STABLE */
        })
      ]),
      footer: _withCtx(() => [
        _createVNode(_component_q_card, {
          flat: "",
          class: "no-border-radius"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_separator),
            _createVNode(_component_q_card_actions, { align: "right" }, {
              default: _withCtx(() => [
                (!_unref(isMobile))
                  ? (_openBlock(), _createBlock(QBtnCancel, {
                      key: 0,
                      label: _unref($t)('signup.buttons.cancel.label'),
                      type: 'button',
                      onClick: onCancelClick,
                      "icon-left": 'close',
                      class: "q-mr-xs"
                    }, null, 8 /* PROPS */, ["label"]))
                  : _createCommentVNode("v-if", true),
                _createVNode(QBtnValid, {
                  label: _unref($t)('signup.buttons.ok.label'),
                  type: 'submit',
                  onClick: onOkClick,
                  loading: loading.value,
                  disable: loading.value,
                  "loading-label": _unref($t)('signin.loader.label'),
                  "icon-right": chkAcceptRules.value ? 'login' : 'block'
                }, null, 8 /* PROPS */, ["label", "loading", "disable", "loading-label", "icon-right"])
              ]),
              _: 1 /* STABLE */
            })
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["show-dialog", "title", "maximized", "dialog-size"]),
    _createVNode(DiagGeneric, {
      show: showMentionsLegales.value,
      title: 'Mentions légales',
      ok: { label: 'Fermer' },
      "hide-cancel": true,
      onClose: _cache[9] || (_cache[9] = ($event: any) => (showMentionsLegales.value = false)),
      onCancel: _cache[10] || (_cache[10] = ($event: any) => (showMentionsLegales.value = false)),
      onValid: _cache[11] || (_cache[11] = ($event: any) => (showMentionsLegales.value = false))
    }, {
      custom: _withCtx(() => [
        _createVNode(MentionsLegalesPage, { "display-title": false })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["show"]),
    _createVNode(DiagGeneric, {
      show: showCgu.value,
      title: 'Conditions d\'utilisation',
      ok: { label: 'Fermer' },
      "hide-cancel": true,
      onClose: _cache[12] || (_cache[12] = ($event: any) => (showCgu.value = false)),
      onCancel: _cache[13] || (_cache[13] = ($event: any) => (showCgu.value = false)),
      onValid: _cache[14] || (_cache[14] = ($event: any) => (showCgu.value = false)),
      class: "dialog-container"
    }, {
      custom: _withCtx(() => [
        _createVNode(CGUPage, { "display-title": false })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["show"])
  ], 64 /* STABLE_FRAGMENT */))
}
}

})